import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { leaveAPI, clientSettingsAPI } from '../../services';

// Leave type colors
const leaveTypeColors = {
  sick: 'bg-red-100 text-red-800',
  casual: 'bg-blue-100 text-blue-800',
  earned: 'bg-green-100 text-green-800',
  unpaid: 'bg-gray-100 text-gray-800',
  compensatory: 'bg-purple-100 text-purple-800',
};

// Status colors
const statusColors = {
  pending: 'bg-yellow-100 text-yellow-800',
  approved: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
  cancelled: 'bg-gray-100 text-gray-800',
};

// Leave types that build up through the year via the credit ledger.
const ACCRUING_LEAVE_TYPES = [
  { key: 'sick', label: 'Sick Leave', icon: '🏥', border: 'border-l-red-500', usedBar: 'bg-red-500' },
  { key: 'casual', label: 'Casual Leave', icon: '🏖️', border: 'border-l-blue-500', usedBar: 'bg-blue-500' },
  { key: 'earned', label: 'Earned Leave', icon: '🌴', border: 'border-l-green-500', usedBar: 'bg-green-500' },
  { key: 'compensatory', label: 'Comp Off', icon: '⏰', border: 'border-l-purple-500', usedBar: 'bg-purple-500' }
];

// Credits are whole or half days — render 1.5 as "1.5" and 2 as "2".
const formatDays = (n) => {
  const value = Number(n) || 0;
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
};

const CREDIT_SOURCE_LABELS = {
  accrual: 'Monthly credit',
  opening: 'Opening balance',
  carry_forward: 'Carried forward',
  adjustment: 'Admin adjustment',
  comp_off: 'Comp off earned'
};

const LeaveManagement = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('balance');
  const [leaveBalance, setLeaveBalance] = useState(null);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [pendingLeaves, setPendingLeaves] = useState([]);
  const [monthlyTracking, setMonthlyTracking] = useState(null);
  const [trackingYear, setTrackingYear] = useState(new Date().getFullYear());
  const [creditHistory, setCreditHistory] = useState(null);
  const [creditType, setCreditType] = useState('casual');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  // Org-level fallback for the working-days check below, when the employee has no shift assigned
  const [clientWorkTimings, setClientWorkTimings] = useState(null);

  // Get minimum date for leave application (employee's date of joining)
  const getMinLeaveDate = () => {
    if (user?.dateOfJoining) {
      return new Date(user.dateOfJoining).toISOString().split('T')[0];
    }
    return ''; // No restriction if DOJ not set
  };
  
  // Apply leave form state
  const [showApplyForm, setShowApplyForm] = useState(false);
  const [applyForm, setApplyForm] = useState({
    leaveType: 'casual',
    startDate: '',
    endDate: '',
    reason: '',
    isHalfDay: false,
    halfDayType: 'first_half',
  });

  // Review modal state
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [selectedLeave, setSelectedLeave] = useState(null);
  const [reviewComments, setReviewComments] = useState('');

  const isAdminOrManager = ['admin', 'manager', 'superadmin'].includes(user?.role);

  useEffect(() => {
    fetchData();
  }, [activeTab, trackingYear]);

  useEffect(() => {
    // Fetch once - used as a fallback when the employee has no shift assigned,
    // matching the backend's own fallback order (shift > client workTimings > Mon-Fri)
    clientSettingsAPI.getMySettings()
      .then(res => { if (res.status === 'success') setClientWorkTimings(res.data?.workTimings || null); })
      .catch(err => console.error('Failed to fetch client work timings:', err));
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      if (activeTab === 'balance') {
        const response = await leaveAPI.getLeaveBalance();
        setLeaveBalance(response.data);
      } else if (activeTab === 'requests') {
        const response = await leaveAPI.getLeaveRequests();
        setLeaveRequests(response.data || []);
      } else if (activeTab === 'pending' && isAdminOrManager) {
        const response = await leaveAPI.getPendingLeaves();
        setPendingLeaves(response.data || []);
      } else if (activeTab === 'tracking') {
        const response = await leaveAPI.getMonthlyTracking({ year: trackingYear });
        setMonthlyTracking(response.data);
      } else if (activeTab === 'credits') {
        const response = await leaveAPI.getLeaveCreditHistory({ year: trackingYear });
        setCreditHistory(response.data);
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleApplyLeave = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    // Validate start date <= end date
    if (!applyForm.isHalfDay && applyForm.startDate && applyForm.endDate && applyForm.startDate > applyForm.endDate) {
      setError('End date must be on or after start date');
      return;
    }

    // Helper to get week of month (1-5)
    const getWeekOfMonth = (date) => {
      const firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
      const dayOfMonth = date.getDate();
      const firstDayWeekday = firstDay.getDay();
      return Math.ceil((dayOfMonth + firstDayWeekday) / 7);
    };

    // Calculate number of working days using weekoff configuration
    const calculateWorkingDays = (start, end) => {
      let count = 0;
      const current = new Date(start);
      const endDate = new Date(end);
      
      // Get weekoff configuration: employee's shift takes priority, then the
      // org's own working-days settings, then Mon-Fri as a last resort.
      // (Must mirror the backend's getWeekoffConfig() fallback order exactly -
      // this is only a pre-submit UI check; the server is still authoritative.)
      const weekoffType = user?.shift?.weekoffType || clientWorkTimings?.weekoffType || 'fixed';
      const workingDays = user?.shift?.workingDays || clientWorkTimings?.workingDays || [1, 2, 3, 4, 5];
      const rotationWeekoffs = user?.shift?.rotationWeekoffs || clientWorkTimings?.rotationWeekoffs || [];
      
      while (current <= endDate) {
        const dayOfWeek = current.getDay();
        let isWeekoffDay = false;
        
        if (weekoffType === 'fixed') {
          isWeekoffDay = !workingDays.includes(dayOfWeek);
        } else if (weekoffType === 'rotation') {
          const weekNumber = getWeekOfMonth(current);
          const weekConfig = rotationWeekoffs.find(r => r.weekNumber === weekNumber);
          if (weekConfig) {
            isWeekoffDay = weekConfig.weekoffDays.includes(dayOfWeek);
          } else if (rotationWeekoffs.length > 0) {
            // Fallback to last week's pattern
            const lastWeekConfig = rotationWeekoffs[rotationWeekoffs.length - 1];
            isWeekoffDay = lastWeekConfig.weekoffDays.includes(dayOfWeek);
          }
        }
        
        if (!isWeekoffDay) count++;
        current.setDate(current.getDate() + 1);
      }
      return count;
    };

    const numberOfDays = applyForm.isHalfDay ? 0.5 : calculateWorkingDays(applyForm.startDate, applyForm.endDate);
    
    // Validate balance for non-unpaid leave types
    if (applyForm.leaveType !== 'unpaid' && leaveBalance?.balance) {
      const balance = leaveBalance.balance[applyForm.leaveType];
      if (balance) {
        const available = (balance.total || 0) - (balance.used || 0) - (balance.pending || 0);
        if (numberOfDays > available) {
          setError(`Insufficient ${applyForm.leaveType} leave balance. Available: ${available} days, Requested: ${numberOfDays} days`);
          return;
        }
      }
    }

    if (numberOfDays === 0) {
      setError('Selected dates contain only weekends. Please select at least one working day.');
      return;
    }
    
    try {
      await leaveAPI.applyLeave(applyForm);
      setSuccess('Leave request submitted successfully!');
      setShowApplyForm(false);
      setApplyForm({
        leaveType: 'casual',
        startDate: '',
        endDate: '',
        reason: '',
        isHalfDay: false,
        halfDayType: 'first_half',
      });
      fetchData();
    } catch (err) {
      setError(err.message || 'Failed to submit leave request');
    }
  };

  const handleApprove = async (id) => {
    try {
      await leaveAPI.approveLeave(id, reviewComments);
      setSuccess('Leave approved successfully!');
      setShowReviewModal(false);
      setSelectedLeave(null);
      setReviewComments('');
      fetchData();
    } catch (err) {
      setError(err.message || 'Failed to approve leave');
    }
  };

  const handleReject = async (id) => {
    if (!reviewComments.trim()) {
      setError('Please provide a reason for rejection');
      return;
    }
    try {
      await leaveAPI.rejectLeave(id, reviewComments);
      setSuccess('Leave rejected');
      setShowReviewModal(false);
      setSelectedLeave(null);
      setReviewComments('');
      fetchData();
    } catch (err) {
      setError(err.message || 'Failed to reject leave');
    }
  };

  const handleCancel = async (id) => {
    if (window.confirm('Are you sure you want to cancel this leave request?')) {
      try {
        await leaveAPI.cancelLeave(id);
        setSuccess('Leave request cancelled');
        fetchData();
      } catch (err) {
        setError(err.message || 'Failed to cancel leave');
      }
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Leave Management</h1>
        <p className="text-gray-600">Manage your leaves and requests</p>
      </div>

      {/* Alerts */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
          <button onClick={() => setError('')} className="float-right font-bold">×</button>
        </div>
      )}
      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
          {success}
          <button onClick={() => setSuccess('')} className="float-right font-bold">×</button>
        </div>
      )}

      {/* Tabs */}
      <div className="mb-6 border-b border-gray-200">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab('balance')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'balance'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Leave Balance
          </button>
          <button
            onClick={() => setActiveTab('credits')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'credits'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Credit History
          </button>
          <button
            onClick={() => setActiveTab('requests')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'requests'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            My Requests
          </button>
          <button
            onClick={() => setActiveTab('tracking')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'tracking'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Monthly Tracking
          </button>
          {isAdminOrManager && (
            <button
              onClick={() => setActiveTab('pending')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'pending'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Pending Approvals
              {pendingLeaves.length > 0 && (
                <span className="ml-2 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                  {pendingLeaves.length}
                </span>
              )}
            </button>
          )}
        </nav>
      </div>

      {/* Action Button */}
      <div className="mb-6">
        <button
          onClick={() => setShowApplyForm(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Apply for Leave
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <>
          {/* Leave Balance Tab */}
          {activeTab === 'balance' && leaveBalance && (
            <div>
              {/* Leave Policy Info */}
              {leaveBalance.policy && (
                <div className="mb-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center gap-2 text-sm text-blue-800">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="font-medium">Leave Policy:</span>
                    <span className="capitalize">{leaveBalance.policy.accrualMethod || 'Monthly'} credit</span>
                    {leaveBalance.policy.carryForward?.enabled && (
                      <span className="ml-2">• Carry forward up to {leaveBalance.policy.carryForward.maxDays || 30} days</span>
                    )}
                  </div>
                </div>
              )}
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {ACCRUING_LEAVE_TYPES.filter((t) => leaveBalance.balance[t.key]?.enabled !== false).map((type) => {
                const bal = leaveBalance.balance[type.key];
                const accrual = bal.accrual || {};
                const target = accrual.effectiveEntitlement ?? accrual.annualEntitlement ?? bal.total;
                const credited = bal.total || 0;
                return (
                  <div key={type.key} className={`bg-white rounded-xl shadow-sm border p-6 border-l-4 ${type.border}`}>
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="font-semibold text-gray-900">{type.label}</h3>
                      <span className="text-2xl">{type.icon}</span>
                    </div>

                    {/* What has actually been earned so far, not the yearly figure */}
                    <div className="mb-3">
                      <div className="flex items-baseline gap-2">
                        <span className="text-3xl font-bold text-green-600">{formatDays(bal.available)}</span>
                        <span className="text-sm text-gray-500">available</span>
                      </div>
                      {target > 0 && (
                        <p className="text-xs text-gray-500 mt-1">
                          {formatDays(credited)} credited so far of {formatDays(target)} this year
                          {accrual.proRated && <span className="text-gray-400"> (from your joining month)</span>}
                        </p>
                      )}
                    </div>

                    {/* Accrual progress: how far through the year's credits you are */}
                    {target > 0 && (
                      <div className="mb-3">
                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden flex">
                          <div
                            className={`h-full ${type.usedBar}`}
                            style={{ width: `${Math.min((bal.used / target) * 100, 100)}%` }}
                            title={`Used ${formatDays(bal.used)}`}
                          />
                          <div
                            className="h-full bg-green-400"
                            style={{ width: `${Math.min((Math.max(credited - bal.used, 0) / target) * 100, 100)}%` }}
                            title={`Available ${formatDays(bal.available)}`}
                          />
                        </div>
                      </div>
                    )}

                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Credited so far</span>
                        <span className="font-medium">{formatDays(credited)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Used</span>
                        <span className="font-medium text-red-600">{formatDays(bal.used)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Pending approval</span>
                        <span className="font-medium text-yellow-600">{formatDays(bal.pending)}</span>
                      </div>
                      {accrual.remainingToAccrue > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500">Still to be credited</span>
                          <span className="font-medium text-gray-600">{formatDays(accrual.remainingToAccrue)}</span>
                        </div>
                      )}
                    </div>

                    {accrual.nextCredit && (
                      <p className="mt-3 pt-3 border-t text-xs text-blue-600">
                        Next credit: {formatDays(accrual.nextCredit.days)} on 1 {accrual.nextCredit.monthName}
                      </p>
                    )}
                  </div>
                );
              })}

              {/* Unpaid Leave */}
              <div className="bg-white rounded-xl shadow-sm border p-6 border-l-4 border-l-gray-400">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-gray-900">Unpaid Leave</h3>
                  <span className="text-2xl">📋</span>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Used</span>
                    <span className="font-medium text-red-600">{formatDays(leaveBalance.balance.unpaid.used)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Pending</span>
                    <span className="font-medium text-yellow-600">{formatDays(leaveBalance.balance.unpaid.pending)}</span>
                  </div>
                  <div className="border-t pt-2 flex justify-between">
                    <span className="text-gray-500 text-xs">No limit for unpaid leave</span>
                  </div>
                </div>
              </div>
            </div>
            </div>
          )}

          {/* Credit History Tab — how the balance was built up, month by month */}
          {activeTab === 'credits' && creditHistory && (() => {
            const available = creditHistory.types || [];
            const selected = available.find((t) => t.leaveType === creditType) || available[0];
            const autoCreditOff = creditHistory.autoCredit?.enabled === false;

            if (!selected) {
              return (
                <div className="bg-white rounded-xl shadow-sm border p-8 text-center text-gray-500">
                  No leave types are enabled for your organisation yet.
                </div>
              );
            }

            return (
              <div className="space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex flex-wrap gap-2">
                    {available.map((t) => {
                      const meta = ACCRUING_LEAVE_TYPES.find((m) => m.key === t.leaveType);
                      return (
                        <button
                          key={t.leaveType}
                          onClick={() => setCreditType(t.leaveType)}
                          className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                            selected.leaveType === t.leaveType
                              ? 'bg-blue-600 text-white border-blue-600'
                              : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          {meta?.icon} {meta?.label || t.leaveType}
                        </button>
                      );
                    })}
                  </div>
                  <select
                    value={trackingYear}
                    onChange={(e) => setTrackingYear(parseInt(e.target.value))}
                    className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {[0, 1, 2].map((back) => {
                      const y = new Date().getFullYear() - back;
                      return <option key={y} value={y}>{y}</option>;
                    })}
                  </select>
                </div>

                {/* Summary strip */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="bg-white rounded-xl border p-4">
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Credited so far</p>
                    <p className="text-2xl font-bold text-gray-900">{formatDays(selected.creditedSoFar)}</p>
                  </div>
                  <div className="bg-white rounded-xl border p-4">
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Used</p>
                    <p className="text-2xl font-bold text-red-600">{formatDays(selected.used)}</p>
                  </div>
                  <div className="bg-white rounded-xl border p-4">
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Available</p>
                    <p className="text-2xl font-bold text-green-600">{formatDays(selected.available)}</p>
                  </div>
                  <div className="bg-white rounded-xl border p-4">
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Still to accrue</p>
                    <p className="text-2xl font-bold text-gray-500">{formatDays(selected.remainingToAccrue)}</p>
                  </div>
                </div>

                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
                  {autoCreditOff ? (
                    <>Leave is credited manually by your HR team for this organisation.</>
                  ) : selected.accrualMethod === 'manual' ? (
                    <>Comp off is granted by your HR team rather than earned on a schedule.</>
                  ) : (
                    <>
                      You earn <span className="font-medium">{formatDays(selected.monthlyRate)} day(s)</span> of{' '}
                      {ACCRUING_LEAVE_TYPES.find((m) => m.key === selected.leaveType)?.label.toLowerCase()} a month
                      {selected.accrualMethod !== 'monthly' && <> ({selected.accrualMethod} credit)</>}
                      , adding up to {formatDays(selected.effectiveEntitlement)} for {creditHistory.year}
                      {selected.proRated && <> (pro-rated from your joining month)</>}.
                      {' '}Anything you do not use stays in your balance.
                    </>
                  )}
                </div>

                {/* Month-by-month ledger */}
                <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Month</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Credited</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Used</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Balance built up</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Note</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {selected.rows.map((row) => {
                          const isFuture = row.status === 'scheduled' || row.status === 'pending';
                          return (
                            <tr key={row.month} className={isFuture ? 'bg-gray-50/60' : ''}>
                              <td className={`px-4 py-3 text-sm ${isFuture ? 'text-gray-400' : 'text-gray-900 font-medium'}`}>
                                {row.monthName}
                              </td>
                              <td className={`px-4 py-3 text-sm text-right ${isFuture ? 'text-gray-400' : 'text-green-600 font-medium'}`}>
                                {row.status === 'credited' ? `+${formatDays(row.credited)}` : `(${formatDays(row.scheduled)})`}
                              </td>
                              <td className={`px-4 py-3 text-sm text-right ${row.used > 0 ? 'text-red-600 font-medium' : 'text-gray-400'}`}>
                                {row.used > 0 ? `−${formatDays(row.used)}` : '—'}
                              </td>
                              <td className={`px-4 py-3 text-sm text-right ${isFuture ? 'text-gray-400' : 'text-gray-900 font-semibold'}`}>
                                {formatDays(row.runningCredited)}
                              </td>
                              <td className="px-4 py-3 text-xs text-gray-500">
                                {row.status === 'credited'
                                  ? row.sources.map((src, i) => (
                                      <span key={i} className="inline-block mr-2">
                                        {CREDIT_SOURCE_LABELS[src.source] || src.source}
                                        {src.note && <span className="text-gray-400"> — {src.note}</span>}
                                      </span>
                                    ))
                                  : row.status === 'manual'
                                    ? 'Credited manually'
                                    : 'Not credited yet'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <div className="px-4 py-3 bg-gray-50 border-t text-xs text-gray-500">
                    Amounts in brackets are scheduled for later in the year and are not part of your balance yet.
                  </div>
                </div>
              </div>
            );
          })()}

          {/* My Requests Tab */}
          {activeTab === 'requests' && (
            <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Dates</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Days</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reason</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {leaveRequests.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                        No leave requests found
                      </td>
                    </tr>
                  ) : (
                    leaveRequests.map((leave) => (
                      <tr key={leave._id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${leaveTypeColors[leave.leaveType]}`}>
                            {leave.leaveType}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {formatDate(leave.startDate)} - {formatDate(leave.endDate)}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {leave.numberOfDays} {leave.isHalfDay && '(Half Day)'}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                          {leave.reason}
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${statusColors[leave.status]}`}>
                            {leave.status}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          {(leave.status === 'pending' || (leave.status === 'approved' && new Date(leave.startDate) > new Date())) && (
                            <button
                              onClick={() => handleCancel(leave._id)}
                              className="text-red-600 hover:text-red-800 text-sm font-medium"
                            >
                              Cancel
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Pending Approvals Tab */}
          {activeTab === 'pending' && isAdminOrManager && (
            <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employee</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Dates</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Days</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reason</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {pendingLeaves.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                        No pending leave requests
                      </td>
                    </tr>
                  ) : (
                    pendingLeaves.map((leave) => (
                      <tr key={leave._id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div className="flex items-center">
                            <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                              <span className="text-blue-600 font-medium">
                                {leave.employee?.name?.charAt(0) || 'U'}
                              </span>
                            </div>
                            <div className="ml-3">
                              <p className="text-sm font-medium text-gray-900">{leave.employee?.name}</p>
                              <p className="text-xs text-gray-500">{leave.employee?.department}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${leaveTypeColors[leave.leaveType]}`}>
                            {leave.leaveType}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {formatDate(leave.startDate)} - {formatDate(leave.endDate)}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {leave.numberOfDays}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                          {leave.reason}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex gap-2">
                            <button
                              onClick={() => {
                                setSelectedLeave(leave);
                                setShowReviewModal(true);
                              }}
                              className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700"
                            >
                              Review
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Monthly Tracking Tab */}
          {activeTab === 'tracking' && monthlyTracking && (
            <div className="space-y-6">
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl p-6 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-blue-100 text-sm">This Month</p>
                      <p className="text-3xl font-bold">{monthlyTracking.totalLeavesThisMonth || 0}</p>
                      <p className="text-blue-100 text-xs mt-1">days used in {monthlyTracking.currentMonth}</p>
                    </div>
                    <div className="text-4xl">📅</div>
                  </div>
                </div>
                <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-xl p-6 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-green-100 text-sm">This Year ({trackingYear})</p>
                      <p className="text-3xl font-bold">{monthlyTracking.totalLeavesThisYear || 0}</p>
                      <p className="text-green-100 text-xs mt-1">total days used</p>
                    </div>
                    <div className="text-4xl">📊</div>
                  </div>
                </div>
                <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl p-6 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-purple-100 text-sm">Since Joining</p>
                      <p className="text-3xl font-bold">{monthlyTracking.totalLeavesSinceJoining || 0}</p>
                      <p className="text-purple-100 text-xs mt-1">
                        {monthlyTracking.employee?.dateOfJoining && 
                          `from ${formatDate(monthlyTracking.employee.dateOfJoining)}`
                        }
                      </p>
                    </div>
                    <div className="text-4xl">🎯</div>
                  </div>
                </div>
              </div>

              {/* Year Selector */}
              <div className="flex items-center gap-4">
                <label className="text-sm font-medium text-gray-700">Select Year:</label>
                <select
                  value={trackingYear}
                  onChange={(e) => setTrackingYear(parseInt(e.target.value))}
                  className="border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {monthlyTracking.yearlySummary?.map(year => (
                    <option key={year.year} value={year.year}>{year.year}</option>
                  ))}
                </select>
              </div>

              {/* Monthly Breakdown Table */}
              <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
                <div className="px-6 py-4 bg-gray-50 border-b">
                  <h3 className="text-lg font-semibold text-gray-900">Monthly Breakdown - {trackingYear}</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Month</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-red-500 uppercase">Sick</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-blue-500 uppercase">Casual</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-green-500 uppercase">Earned</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-purple-500 uppercase">Comp-Off</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Unpaid</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-900 uppercase">Total</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {monthlyTracking.monthlyData?.map((month, index) => (
                        <tr key={index} className={`
                          ${month.status === 'not_joined' ? 'bg-gray-50 text-gray-400' : ''}
                          ${month.status === 'future' ? 'bg-gray-50 text-gray-400' : ''}
                          ${month.month === monthlyTracking.currentMonth && month.year === new Date().getFullYear() ? 'bg-blue-50' : ''}
                          hover:bg-gray-50
                        `}>
                          <td className="px-4 py-3 font-medium">
                            {month.month}
                            {month.month === monthlyTracking.currentMonth && month.year === new Date().getFullYear() && (
                              <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">Current</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-center">
                            {month.status === 'active' && month.sick > 0 ? (
                              <span className="bg-red-100 text-red-700 px-2 py-1 rounded">{month.sick}</span>
                            ) : (
                              <span className="text-gray-300">-</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-center">
                            {month.status === 'active' && month.casual > 0 ? (
                              <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded">{month.casual}</span>
                            ) : (
                              <span className="text-gray-300">-</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-center">
                            {month.status === 'active' && month.earned > 0 ? (
                              <span className="bg-green-100 text-green-700 px-2 py-1 rounded">{month.earned}</span>
                            ) : (
                              <span className="text-gray-300">-</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-center">
                            {month.status === 'active' && month.compensatory > 0 ? (
                              <span className="bg-purple-100 text-purple-700 px-2 py-1 rounded">{month.compensatory}</span>
                            ) : (
                              <span className="text-gray-300">-</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-center">
                            {month.status === 'active' && month.unpaid > 0 ? (
                              <span className="bg-gray-200 text-gray-700 px-2 py-1 rounded">{month.unpaid}</span>
                            ) : (
                              <span className="text-gray-300">-</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-center font-bold">
                            {month.status === 'active' ? (
                              <span className={`${month.total > 0 ? 'text-gray-900' : 'text-gray-400'}`}>
                                {month.total}
                              </span>
                            ) : (
                              <span className="text-gray-300">-</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-center">
                            {month.status === 'not_joined' && (
                              <span className="text-xs text-gray-400">Not Joined</span>
                            )}
                            {month.status === 'future' && (
                              <span className="text-xs text-gray-400">Future</span>
                            )}
                            {month.status === 'active' && month.total === 0 && (
                              <span className="text-xs text-green-500">✓ Full Attendance</span>
                            )}
                            {month.status === 'active' && month.total > 0 && (
                              <span className="text-xs text-orange-500">Leaves Taken</span>
                            )}
                          </td>
                        </tr>
                      ))}
                      {/* Total Row */}
                      <tr className="bg-gray-100 font-bold">
                        <td className="px-4 py-3">Year Total</td>
                        <td className="px-4 py-3 text-center text-red-600">
                          {monthlyTracking.monthlyData?.reduce((sum, m) => sum + (m.sick || 0), 0)}
                        </td>
                        <td className="px-4 py-3 text-center text-blue-600">
                          {monthlyTracking.monthlyData?.reduce((sum, m) => sum + (m.casual || 0), 0)}
                        </td>
                        <td className="px-4 py-3 text-center text-green-600">
                          {monthlyTracking.monthlyData?.reduce((sum, m) => sum + (m.earned || 0), 0)}
                        </td>
                        <td className="px-4 py-3 text-center text-purple-600">
                          {monthlyTracking.monthlyData?.reduce((sum, m) => sum + (m.compensatory || 0), 0)}
                        </td>
                        <td className="px-4 py-3 text-center text-gray-600">
                          {monthlyTracking.monthlyData?.reduce((sum, m) => sum + (m.unpaid || 0), 0)}
                        </td>
                        <td className="px-4 py-3 text-center text-gray-900">
                          {monthlyTracking.totalLeavesThisYear || 0}
                        </td>
                        <td className="px-4 py-3 text-center"></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Yearly Summary */}
              {monthlyTracking.yearlySummary?.length > 1 && (
                <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
                  <div className="px-6 py-4 bg-gray-50 border-b">
                    <h3 className="text-lg font-semibold text-gray-900">Yearly Summary (Since Joining)</h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Year</th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-red-500 uppercase">Sick</th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-blue-500 uppercase">Casual</th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-green-500 uppercase">Earned</th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-purple-500 uppercase">Comp-Off</th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Unpaid</th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-gray-900 uppercase">Total</th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Leave Count</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {monthlyTracking.yearlySummary?.map((year, index) => (
                          <tr key={index} className={`hover:bg-gray-50 ${year.year === trackingYear ? 'bg-blue-50' : ''}`}>
                            <td className="px-4 py-3 font-medium">
                              {year.year}
                              {year.year === new Date().getFullYear() && (
                                <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">Current</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-center text-red-600">{year.sick}</td>
                            <td className="px-4 py-3 text-center text-blue-600">{year.casual}</td>
                            <td className="px-4 py-3 text-center text-green-600">{year.earned}</td>
                            <td className="px-4 py-3 text-center text-purple-600">{year.compensatory}</td>
                            <td className="px-4 py-3 text-center text-gray-600">{year.unpaid}</td>
                            <td className="px-4 py-3 text-center font-bold">{year.total}</td>
                            <td className="px-4 py-3 text-center text-gray-500">{year.leaveCount} requests</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Apply Leave Modal */}
      {showApplyForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Apply for Leave</h2>
              <button onClick={() => setShowApplyForm(false)} className="text-gray-400 hover:text-gray-600">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleApplyLeave} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Leave Type</label>
                <select
                  value={applyForm.leaveType}
                  onChange={(e) => setApplyForm({ ...applyForm, leaveType: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  {(!leaveBalance?.policy?.leaveTypes || leaveBalance?.policy?.leaveTypes?.casual?.enabled !== false) && (
                    <option value="casual">Casual Leave {leaveBalance?.balance?.casual?.available > 0 && `(${leaveBalance.balance.casual.available} available)`}</option>
                  )}
                  {(!leaveBalance?.policy?.leaveTypes || leaveBalance?.policy?.leaveTypes?.sick?.enabled !== false) && (
                    <option value="sick">Sick Leave {leaveBalance?.balance?.sick?.available > 0 && `(${leaveBalance.balance.sick.available} available)`}</option>
                  )}
                  {(!leaveBalance?.policy?.leaveTypes || leaveBalance?.policy?.leaveTypes?.earned?.enabled !== false) && (
                    <option value="earned">Earned Leave {leaveBalance?.balance?.earned?.available > 0 && `(${leaveBalance.balance.earned.available} available)`}</option>
                  )}
                  <option value="unpaid">Unpaid Leave</option>
                  {(!leaveBalance?.policy?.leaveTypes || leaveBalance?.policy?.leaveTypes?.compensatory?.enabled !== false) && leaveBalance?.balance?.compensatory?.available > 0 && (
                    <option value="compensatory">Compensatory Off ({leaveBalance.balance.compensatory.available} available)</option>
                  )}
                </select>
              </div>

              <div className="flex items-center gap-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={applyForm.isHalfDay}
                    onChange={(e) => setApplyForm({ ...applyForm, isHalfDay: e.target.checked })}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">Half Day</span>
                </label>
                {applyForm.isHalfDay && (
                  <select
                    value={applyForm.halfDayType}
                    onChange={(e) => setApplyForm({ ...applyForm, halfDayType: e.target.value })}
                    className="border rounded-lg px-3 py-1 text-sm"
                  >
                    <option value="first_half">First Half</option>
                    <option value="second_half">Second Half</option>
                  </select>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                  <input
                    type="date"
                    value={applyForm.startDate}
                    onChange={(e) => setApplyForm({ ...applyForm, startDate: e.target.value })}
                    min={getMinLeaveDate()}
                    className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Backdated leave applications are allowed
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                  <input
                    type="date"
                    value={applyForm.endDate}
                    onChange={(e) => setApplyForm({ ...applyForm, endDate: e.target.value })}
                    min={applyForm.startDate || getMinLeaveDate()}
                    className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                    disabled={applyForm.isHalfDay}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
                <textarea
                  value={applyForm.reason}
                  onChange={(e) => setApplyForm({ ...applyForm, reason: e.target.value })}
                  rows={3}
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter reason for leave..."
                  required
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowApplyForm(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Submit Request
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Review Modal */}
      {showReviewModal && selectedLeave && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Review Leave Request</h2>
              <button onClick={() => setShowReviewModal(false)} className="text-gray-400 hover:text-gray-600">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-3 mb-4">
              <div className="flex justify-between">
                <span className="text-gray-500">Employee:</span>
                <span className="font-medium">{selectedLeave.employee?.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Type:</span>
                <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${leaveTypeColors[selectedLeave.leaveType]}`}>
                  {selectedLeave.leaveType}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Dates:</span>
                <span className="font-medium">
                  {formatDate(selectedLeave.startDate)} - {formatDate(selectedLeave.endDate)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Days:</span>
                <span className="font-medium">{selectedLeave.numberOfDays}</span>
              </div>
              <div>
                <span className="text-gray-500">Reason:</span>
                <p className="mt-1 text-gray-900">{selectedLeave.reason}</p>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Comments (required for rejection)</label>
              <textarea
                value={reviewComments}
                onChange={(e) => setReviewComments(e.target.value)}
                rows={2}
                className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Add comments..."
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => handleReject(selectedLeave._id)}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Reject
              </button>
              <button
                onClick={() => handleApprove(selectedLeave._id)}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                Approve
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeaveManagement;
